import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ActivatedRoute } from '@angular/router';
import { ApplicationNotes, EditNoteDialogResult, isAdmin, isInternalUser, PaginablePayloadApiResponse, RemoveApplicationNoteByNoteIdFn, UpdateApplicationNoteFn, User } from '@portal-workspace/grow-shared-library';
import { tap } from 'rxjs/operators';
import { getUser, setupUntilDestroy } from '../component-utils';
import { PageEvent } from '@angular/material/paginator';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { ApplicationDialogService } from '../application-dialog-component/application-dialog.service';
import { ConfirmationDialogResult, DEFAULT_LIMIT, DEFAULT_OFFSET, } from '@portal-workspace/grow-shared-library'
import { PortalHotToastService } from '../portal-hot-toast-component/hot-toast.service';
import { MatTableModule } from '@angular/material/table';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { CustomPaginatorComponent } from '../custom-paginator-component/custom-paginator/custom-paginator.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CollapsableExpandableTextComponent } from '../collapsable-expandable-component/collapsable-expandable-text.component';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass, DatePipe } from '@angular/common';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatSortModule } from '@angular/material/sort';

export type GetNotesByApplicationIdFn = (limit: number, offset: number, applicationId: number, creditNotesOnly?: boolean) => Observable<PaginablePayloadApiResponse<ApplicationNotes>>;

export class ApplicationNotesDataSource extends DataSource<ApplicationNotes> {

  subject: Subject<ApplicationNotes[]> = new BehaviorSubject<ApplicationNotes[]>([]);

  connect(collectionViewer: CollectionViewer): Observable<ApplicationNotes[]> {
    return this.subject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.subject.complete();
  }

  update(disbursements: ApplicationNotes[]) {
    this.subject.next(disbursements);
  }
}


@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
    selector: 'notes',
    templateUrl: './notes.component.html',
    styleUrls: ['./notes.component.scss'],
    standalone: true,
    imports: [MatTableModule, MatSortModule, FlexModule, NgClass, ExtendedModule, CollapsableExpandableTextComponent, MatTooltipModule, CustomPaginatorComponent, DatePipe]
})

export class NotesComponent implements OnInit {
  @Input({required: false}) applicationNotes: ApplicationNotes[] | [] = [];
  @Input({required: true}) getNotesByApplicationIdFn!: GetNotesByApplicationIdFn;
  @Input({required: true}) removeApplicationNoteByNoteIdFn!: RemoveApplicationNoteByNoteIdFn;
  @Input({required: false}) updateApplicationNoteFn?: UpdateApplicationNoteFn;
  @Input({required: false}) creditNotesOnly = false;
  @Output() totalNotes: EventEmitter<number> = new EventEmitter<number>();

  subscriptions: Subscription[] = []
  isAdmin = isAdmin;
  isInternaluser = isInternalUser
  user!: User

  dataSource = new ApplicationNotesDataSource();
  displayedColumns: string[] = ['date', 'user', 'note', 'action'];
  @Input({required: true}) total !: number;
  offset = DEFAULT_OFFSET;
  limit = DEFAULT_LIMIT;

  constructor(private route: ActivatedRoute,
    private dialogService: ApplicationDialogService,
    private toastService: PortalHotToastService
  ) { }

  ngOnInit(): void {
    setupUntilDestroy(this);
    this.user = getUser()!
    this.reload()
    this.reset();
  }

  reset() {
    this.limit = DEFAULT_LIMIT;
    this.offset = DEFAULT_OFFSET;
  }

  private reload() {
    const applicationId = this.route.snapshot.paramMap.get('applicationId') || '';
    // credit-note and internal-only filtering is applied server side so `total` matches the rows
    // returned and the paginator stays accurate
    this.getNotesByApplicationIdFn(this.limit, this.offset,parseInt(applicationId), this.creditNotesOnly).pipe(
      tap(r => {
        this.total = r.total;
        this.totalNotes.emit(r.total);
        this.applicationNotes = r.payload;
        this.dataSource.update(this.applicationNotes)
      })
    ).subscribe();
  }

  /**
   * Editing is limited to the note's own author, and only for internal staff, so brokers can never
   * amend notes. Notes that already triggered an email notification stay locked so the portal keeps
   * an accurate record of what was communicated. The same rules are enforced by the update endpoint.
   */
  canEdit(element: ApplicationNotes): boolean {
    return !!this.updateApplicationNoteFn
      && element.UserId === this.user.UserId
      && isInternalUser(this.user)
      && !element.NotificationSubmitter;
  }

  onEdit(element: ApplicationNotes) {
    this.subscriptions.push(
      this.dialogService.openEditNoteDialog({
        title: element.Title ?? null,
        note: element.Note,
      }).afterClosed().pipe(
        tap((r: EditNoteDialogResult | undefined) => {
          if (r && r.readyForSubmission && element.ApplicationNotesId) {
            this.updateNote(element.ApplicationNotesId, r);
          }
        })
      ).subscribe()
    )
  }

  private updateNote(noteId: number, r: EditNoteDialogResult) {
    this.subscriptions.push(
      this.updateApplicationNoteFn!({
        ApplicationNotesId: noteId,
        Title: r.title,
        Note: r.note,
      }).pipe(
        this.toastService.spinnerObservable(),
        this.toastService.snackBarObservable("Note successfully updated"),
        tap(() => { this.reload() }),
      ).subscribe()
    )
  }

  ngOnChanges(changes: any) {
    if (changes.applicationNotes) {
      this.reload()
    }
  }

  onPagination($event: PageEvent) {
    this.offset = $event.pageIndex;
    this.limit = $event.pageSize;
    this.reload();
  }
  deleteNote(noteId: number) {
    this.subscriptions.push(
      this.removeApplicationNoteByNoteIdFn(noteId).pipe(
        tap(r => { this.reload() }),
        this.toastService.snackBarObservable("Note successfully removed")
      ).subscribe()
    )
  }

  onDelete(element: ApplicationNotes) {
    this.subscriptions.push(
      this.dialogService.openConfirmationDialog({
        message: "Please confirm",
        subMessage: "Do you want to delete this note?"
      }).afterClosed().subscribe((r: ConfirmationDialogResult | undefined) => {
        if (r && r.readyForSubmission) {
          if (element.ApplicationNotesId) {
            this.deleteNote(element.ApplicationNotesId)
            this.reload()
          }
        }
      })
    )
  }
}
